import React, {PureComponent} from "react";
import PropTypes from "prop-types";
import ClientSelect from "../Client/ClientSelect";
import {
  Alert,
  Row,
  Input,
  Form,
  FormGroup,
  Label,
  Col,
  FormFeedback, Badge,
} from "reactstrap";
import {toast} from "react-toastify";
import {CVSMSCounter} from "../../utils/smsApi-counter";
import AppDatePicker from "../../componentsReusable/AppDatePicker";
import SmsApiContentLengthModal from "../SmsApiContentLengthModal";
import {get} from "lodash";
import apiPlatform from "../../apiPlatform";
import Button from "../Button";
import AppTableModal from "../../componentsReusable/AppTableModal";
import ClientsTable from "../Client/ClientsTable";
import getClientName from "../../utils/clientName";
import Tooltip from "../../componentsReusable/Tooltip";
import Icon from "../../componentsReusable/Icon";
import PhoneNumber from "../PhoneNumber";
import {withTranslation} from "react-i18next";
import apiPlatformResponseViolationsParser from "../../utils/apiPlatformResponseViolationsParser";

class SmsForm extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      saving: false,
      errors: {},
      data: props.data,
    };
  }
  componentDidMount() {
    CVSMSCounter.activate('smsApi-content', 'smsApi-counter');
  }

  onChangeData = (values) => {
    this.setState({
      data: {
        ...this.state.data,
        ...values,
      }
    })
  }

  removeClientById = clientId => {
    this.onChangeData({
      clients: this.state.data.clients.filter(client => client.id !== clientId)
    })
  }

  save = () => {
    const {data} = this.state;

    this.setState({
      errors: {},
    })

    apiPlatform.smsMessages.create(data)
      .then(() => {
        this.props.onSavingSuccess();

        if(data?.clients.length > 1){
          toast.success(this.props.t('success_msg_many'));
        } else {
          toast.success(this.props.t('success_msg_single'));
        }
      })
      .catch(res => {
        this.setState({
          errors: apiPlatformResponseViolationsParser(res?.response?.data?.violations),
        })
      })
      .finally(() => {
        this.props.onSavingEnd();
      })
  }

  render() {
    const {errors, data} = this.state;

    return <Form onSubmit={e => e.preventDefault()}>
      {errors?.errors && <Row>
        <Col>
          <Alert color="danger">
            <ul className="list-style-none mb-0">
              {errors?.errors.map(err => <li>{err}</li>)}
            </ul>
          </Alert>
        </Col>
      </Row>}
      <FormGroup row>
        <Label xs={3}>
          <span>{this.props.t('field.clients')}</span>
          {data.clients.length > 0 && <span className="ml-2">({data.clients.length})</span>}
        </Label>
        <Col xs={9} className="d-flex flex-row flex-nowrap w-100">
          {
            data.clients.length > 0
              ? <div className="flex-grow-1 d-flex flex-row flex-wrap" style={{maxWidth: 'calc(100% - 42px)', maxHeight: '17vh', overflowY: 'auto'}}>{data.clients.map(client => {
                return <Badge color={'light'} className="mr-1 mb-1 d-flex flex-row flex-nowrap align-items-center" style={{maxWidth: '32%'}}>
                  <Tooltip msg={client?.phoneNumber?.number ?? <i className="text-warning">{this.props.t('field.clients_phone_number_empty')}</i>}><div className="text-ellipsis"><span>{getClientName(client)}</span></div></Tooltip>
                  <Icon tooltip={this.props.t('field.clients_remove_tooltip')} className="fas fa-times cursor-pointer ml-2 text-secondary" onClick={() => this.removeClientById(client.id)} />
                </Badge>
              })}</div>
              : <ClientSelect
                className="flex-grow-1"
                isDisabled={this.props.clientSelectDisabled}
                client={data.client}
                onChange={client => {
                  this.onChangeData({
                    clients: [],
                    client: client?.id,
                    phoneNumber: client?.phoneNumber ?? {...data.phoneNumber, number: null},
                  })
                }}
              />
          }
          {!this.props.clientSelectDisabled && <AppTableModal
            size={'w-100'}
            tableComponent={ClientsTable}
            clickToSelect
            onAccept={(clients) => {
              this.onChangeData({
                clients: [
                  ...data.clients,
                  ...clients.filter(c1 => !data.clients.find(c2 => c2.id === c1.id))
                ],
                client: null,
                phoneNumber: null,
              })
            }}
          >
            <Button color={"secondary"} className="ml-2 flex-grow-1" tooltip={this.props.t('field.add_clients_button')} size="sm"><i className="fas fa-users"/></Button>
          </AppTableModal>}
        </Col>
      </FormGroup>
      {data.clients.length === 0 && <FormGroup row>
        <Label xs={3}>{this.props.t('field.phone_number')}</Label>
        <Col xs={9}>
          <PhoneNumber
            onChange={phoneNumber => this.onChangeData({ phoneNumber })}
            value={{...data.phoneNumber}}
            errorMsg={get(errors, 'phoneNumber.number')?.join('; ')}
          />
        </Col>
      </FormGroup>}
      <FormGroup row>
        <Label xs={3}>{this.props.t('field.send_at')}</Label>
        <Col xs={9} className="d-flex align-items-center">
          {data.sendAt === null && <React.Fragment>
            <Tooltip msg={this.props.t('field.send_at_tooltip')}>
              <Badge color="light" className="py-1 cursor-pointer" onClick={() => this.onChangeData({sendAt: false})}>{this.props.t('field.send_at_now')}</Badge>
            </Tooltip>
          </React.Fragment>}
          {data.sendAt !== null && (
            <AppDatePicker
              autoFocus
              isClearable
              onChange={sendAt => this.onChangeData({sendAt})}
              dateTime
            />
          )}
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label xs={3}>{this.props.t('field.content')}</Label>
        <Col xs={9}>
          <Input type="textarea" id="smsApi-content" data-cv-sms-textarea rows={10} invalid={!!errors?.content} placeholder={this.props.t('field.content')} onChange={e => this.onChangeData({content: e.target.value})} value={data.content} />
          <FormFeedback>{errors?.content?.join('; ')}</FormFeedback>
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label xs={3} className="d-flex justify-content-between align-items-center">
          <span>{this.props.t('field.content_length')}</span>
        </Label>
        <Col xs={9}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column">
              <div data-cv-sms-counter id="smsApi-counter" className="d-flex flex-column">
                <span>{this.props.t('field.content_sms_count')}: <span id="sms-counter" /></span>
                <span>{this.props.t('field.content_char_counter')}: <span id="sms-char-counter" /></span>
              </div>
            </div>
            <SmsApiContentLengthModal />
          </div>
        </Col>
      </FormGroup>
    </Form>;
  }
}

SmsForm.defaultProps = {
  clientSelectDisabled: false,
  data: {
    repairOrder: null,
    clients: [],
    client: null,
    phoneNumber: {
      countryCode: '',
      number: '',
    },
    content: null,
    sendAt: null,
  },
};

SmsForm.propTypes = {
  onSavingEnd: PropTypes.func.isRequired,
  onSavingSuccess: PropTypes.func.isRequired,
  clientSelectDisabled: PropTypes.bool,
  data: PropTypes.shape({
    repairOrder: PropTypes.number,
    client: PropTypes.number,
    clients: PropTypes.array,
    phoneNumber: PropTypes.shape({
      countryCode: PropTypes.string,
      number: PropTypes.string,
    }),
    content: PropTypes.string,
    sendAt: PropTypes.string,
  }).isRequired,
};

export default withTranslation('sms_form', {withRef: true})(SmsForm);
